import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import Navigation from './Navigation';
import SnapshotTile from './SnapshotTile';
import AllBreakoutRoomsContainer from '../../context/AllBreakoutRoomsContext';

const Scene = styled.div<{ navPadding? }>`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;
    padding-bottom: ${(props) => (props.navPadding ? props.navPadding : 0)};
    background-color: ${(props) => props.theme.colors.black};
    overflow: hidden;
`;

const Container = styled.div`
    display: flex;
    flex: 1;
    gap: 20px;
    max-height: 100%;
    max-width: 100%;
`;

const StatusContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    color: ${(props) => props.theme.colors.white};
`;

const Snapshots = styled.div`
    overflow: hidden;
    display: flex;
    align-content: center;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    flex: 1;
`;

const StyledCircularProgress = styled(CircularProgress)`
    color: ${(props) => props.theme.colors.white};
`;

const MARGIN = 5;
const ASPECT_RATIO = 9 / 16;

const AllBreakoutRoomsView = () => {
    const { breakoutNames, currentPage, perPage, totalPages, countCurrentPage, loading, error } =
        useContainer(AllBreakoutRoomsContainer);
    const { t } = useTranslation('all_breakout_rooms');
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [tileArea, setTileArea] = useState<HTMLDivElement | null>(null);

    const navPadding = useMemo(() => (totalPages > 1 ? '20px' : 0), [totalPages]);

    const measuredRef = useCallback((node) => {
        if (!node) return;

        setDimensions({
            width: node.offsetWidth - MARGIN * 2,
            height: node.offsetHeight - MARGIN * 2
        });

        setTileArea(node);
    }, []);

    useLayoutEffect(() => {
        const resize = () => {
            if (!tileArea) return;

            setDimensions({
                width: tileArea.offsetWidth - MARGIN * 2,
                height: tileArea.offsetHeight - MARGIN * 2
            });
        };

        window.addEventListener('resize', resize);

        return () => window.removeEventListener('resize', resize);
    }, [tileArea]);

    const areaFits = useCallback(
        (possibleWidth: number): boolean => {
            const availableWidth = dimensions.width;
            const availableHeight = dimensions.height;

            let totalHeightNeeded: number;
            let rows: number;

            const videosPerRow = Math.floor(availableWidth / possibleWidth);

            if (videosPerRow === 0) {
                rows = 0;
            } else {
                rows = Math.ceil(countCurrentPage / videosPerRow);
            }

            if (rows > 0) {
                totalHeightNeeded = rows * (possibleWidth * ASPECT_RATIO + MARGIN * 2);
            } else {
                totalHeightNeeded = possibleWidth * ASPECT_RATIO + MARGIN * 2;
            }

            return totalHeightNeeded < availableHeight && possibleWidth < availableWidth;
        },
        [dimensions, countCurrentPage]
    );

    const tileWidth = useMemo(() => {
        let max = 0;

        // binary search for maximal width in [50, 5000]
        for (let lower = 50, upper = 5000; lower < upper; ) {
            max = Math.floor((upper + lower) / 2);

            if (max === lower) {
                break;
            }

            if (areaFits(max)) {
                lower = max;
            } else {
                upper = max;
            }
        }

        return max - MARGIN * 2;
    }, [areaFits]);

    const renderSnapshots = useCallback(() => {
        const props = {
            width: tileWidth,
            height: tileWidth * ASPECT_RATIO,
            margin: MARGIN
        };

        return breakoutNames
            .slice((currentPage - 1) * perPage, currentPage * perPage)
            .map((title, i) => <SnapshotTile key={i} breakoutName={title} {...props} />);
    }, [tileWidth, breakoutNames, currentPage, perPage]);

    if (loading) {
        return (
            <Scene>
                <StatusContainer>
                    <StyledCircularProgress />
                </StatusContainer>
            </Scene>
        );
    }

    if (error) {
        return (
            <Scene>
                <StatusContainer>
                    <div>{t('fatal-error')}</div>
                </StatusContainer>
            </Scene>
        );
    }

    return (
        <Scene navPadding={navPadding}>
            <Navigation />
            <Container>
                <Snapshots ref={measuredRef}>{renderSnapshots()}</Snapshots>
            </Container>
        </Scene>
    );
};

export default AllBreakoutRoomsView;
